import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Search, CheckCircle, AlertCircle, X, RotateCcw } from 'lucide-react';
import { patientService } from '../../patient/services/patientService';
import adminBookingService from '../services/adminBookingService';
import HomeCareBookingSection from '../../../components/HomeCareBookingSection';
import toast from 'react-hot-toast';

const AdminBookingPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin Make a Booking';
  }, []);

  // Patient search states (left column)
  const [patientId, setPatientId] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientDOB, setPatientDOB] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Booking form states (right column)
  const [bookingForm, setBookingForm] = useState({
    patientId: '',
    patientName: '',
    reason: '',
    appointmentType: '',
    scheduleType: '',
    specialty: '',
    doctor: '',
    doctor_id: '',
    clinicLocation: '',
    clinicianId: '',
    clinicianName: ''
  });

  // Calendar and time states
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  
  // Real-time availability states (like patient module)
  const [availableTimes, setAvailableTimes] = useState([]);
  const [doctorMonthAvailability, setDoctorMonthAvailability] = useState(null);

  // Dropdown states
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [showClinicDropdown, setShowClinicDropdown] = useState(false);

  // Data states
  const [specialties, setSpecialties] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availableClinics, setAvailableClinics] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [doctorAvailability, setDoctorAvailability] = useState([]);

  // Loading states
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [loadingClinicians, setLoadingClinicians] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);

  // Notification states
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);

  // Toast notification states
  const [toasts, setToasts] = useState([]);

  // Search loading states
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [patientSearchError, setPatientSearchError] = useState('');

  // Refs for dropdowns
  const doctorDropdownRef = useRef(null);
  const clinicDropdownRef = useRef(null);

  // Toast notification functions
  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Patient search handlers
  const handleNewPatient = () => {
    setPatientId('');
    setPatientName('');
    setPatientDOB('');
    setSelectedPatient(null);
    setBookingForm(prev => ({ ...prev, patientId: '', patientName: '' }));
  };

  const handlePatientSearch = async () => {
    setPatientSearchLoading(true);
    setPatientSearchError('');

    try {
      let foundPatient = null;

      // Search by Patient ID if provided
      if (patientId.trim()) {
        console.log('Searching by Patient ID:', patientId);
        foundPatient = await adminBookingService.searchPatientById(patientId.trim());
      }
      // Search by Name + DOB if Patient ID not provided
      else if (patientName.trim()) {
        console.log('Searching by Name and DOB:', { patientName, patientDOB });
        
        // Split name into first and last name
        const nameParts = patientName.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        if (!firstName) {
          throw new Error('Please enter a valid patient name');
        }

        foundPatient = await adminBookingService.searchPatientByNameAndDOB(
          firstName, 
          lastName, 
          patientDOB
        );
      } else {
        throw new Error('Please enter either a Patient ID or Patient Name');
      }

      if (foundPatient) {
        // Success - patient found
        console.log('🔍 Raw patient data from API:', foundPatient);
        console.log('🔍 Patient fields - id:', foundPatient.id, 'patient_id:', foundPatient.patient_id, 'user_id:', foundPatient.user_id);
        console.log('🔍 Field types - id:', typeof foundPatient.id, 'patient_id:', typeof foundPatient.patient_id, 'user_id:', typeof foundPatient.user_id);
        
        const patientData = {
          id: foundPatient.id,                    // Numeric DB ID (use for API relations)
          patient_id: foundPatient.patient_id,    // Formatted ID like "P000002" (display only)
          name: `${foundPatient.first_name} ${foundPatient.last_name}`,
          first_name: foundPatient.first_name,
          last_name: foundPatient.last_name,
          dob: foundPatient.dob,
          user_id: foundPatient.user_id,
          user_email: foundPatient.user_email,
          phone: foundPatient.phone,
          patient_type: foundPatient.patient_type  // Include patient_type for conditional rendering
        };
        
        console.log('🔍 Formatted patient data (id for DB, patient_id for display):', patientData);

        setSelectedPatient(patientData);
        setBookingForm(prev => ({
          ...prev,
          patientId: patientData.patient_id, // Display the formatted ID like "P000002"
          patientName: patientData.name
        }));

        // Show success toast
        addToast(`Patient found: ${patientData.name}`, 'success');
        
        // Clear any previous error
        setPatientSearchError('');
      }
    } catch (error) {
      console.error('Patient search error:', error);
      const errorMessage = error.message || 'Failed to search for patient';
      setPatientSearchError(errorMessage);
      setSelectedPatient(null);
      
      // Show error toast
      addToast(`Patient search failed: ${errorMessage}`, 'error');
    } finally {
      setPatientSearchLoading(false);
    }
  };

  // Booking form handlers
  const handleBookingFormChange = (field, value) => {
    console.log(`🔄 Admin Booking Form Change: ${field} = ${value}`);
    setBookingForm(prev => ({ ...prev, [field]: value }));
    
    // Auto-set schedule type based on appointment type
    if (field === 'appointmentType') {
      let autoScheduleType = '';
      switch (value) {
        case 'General Telehealth':
        case 'Clinical Telehealth':
          autoScheduleType = 'telehealth';
          break;
        case 'Home Care':
          autoScheduleType = 'in-person';
          break;
        default:
          autoScheduleType = '';
      }
      console.log(`📋 Auto-setting schedule type: ${autoScheduleType} for appointment type: ${value}`);
      // Clear clinic location when switching to general telehealth
      if (value === 'general-telehealth') {
        console.log('🚫 Clearing clinic location for general telehealth');
        setBookingForm(prev => ({ ...prev, [field]: value, scheduleType: autoScheduleType, clinicLocation: '' }));
      } else {
        console.log('✅ Keeping clinic location for clinical telehealth');
        setBookingForm(prev => ({ ...prev, [field]: value, scheduleType: autoScheduleType }));
      }
    }
  };

  // Fetch specialties
  useEffect(() => {
    const fetchSpecialties = async () => {
      setLoadingSpecialties(true);
      try {
        const specialtiesData = await adminBookingService.getSpecialties();
        setSpecialties(specialtiesData);
      } catch (error) {
        console.error('Error fetching specialties:', error);
        addToast('Failed to load specialties', 'error');
      } finally {
        setLoadingSpecialties(false);
      }
    };
    
    fetchSpecialties();
  }, []);

  // Fetch all clinics on component mount (independent of doctor selection)
  useEffect(() => {
    const fetchAllClinics = async () => {
      setLoadingClinics(true);
      try {
        const clinicsData = await adminBookingService.getAllClinics();
        setAvailableClinics(clinicsData);
        
        // Don't auto-set clinic to avoid potential state conflicts
      } catch (error) {
        console.error('Error fetching all clinics:', error);
        addToast('Failed to load clinics', 'error');
        setAvailableClinics([]);
      } finally {
        setLoadingClinics(false);
      }
    };
    
    fetchAllClinics();
  }, []);

  // Fetch doctor availability when doctor changes (like patient module)
  useEffect(() => {
    if (bookingForm.doctor_id) {
      fetchDoctorMonthAvailability(currentMonth);
    } else {
      // Clear calendar availability when no doctor is selected
      setDoctorMonthAvailability(null);
    }
  }, [bookingForm.doctor_id, currentMonth]);

  // Handle specialty change
  const handleSpecialtyChange = async (specialty) => {
    setBookingForm(prev => ({ 
      ...prev, 
      specialty, 
      doctor: '', 
      doctor_id: '', 
      clinicLocation: '' 
    }));
    
    if (specialty) {
      setLoadingDoctors(true);
      try {
        const doctorsData = await adminBookingService.getDoctorsBySpecialty(specialty);
        setDoctors(doctorsData);
        
        if (doctorsData.length === 0) {
          addToast(`No doctors found for ${specialty}`, 'warning');
        }
      } catch (error) {
        console.error('Error fetching doctors:', error);
        addToast('Failed to load doctors for selected specialty', 'error');
        setDoctors([]);
      } finally {
        setLoadingDoctors(false);
      }
    } else {
      setDoctors([]);
    }
  };

  // Doctor dropdown handlers
  const toggleDoctorDropdown = () => {
    setShowDoctorDropdown(!showDoctorDropdown);
    setShowClinicDropdown(false);
  };

  const handleDoctorSelect = async (doctor) => {
    setBookingForm(prev => ({
      ...prev,
      doctor: `${doctor.firstname} ${doctor.lastname}`,
      doctor_id: doctor.id,
      clinicLocation: ''
    }));
    setShowDoctorDropdown(false);
    
    // Clear previously selected date/time and availability
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableTimes([]);
    
    // Clinics are already loaded globally - no need to fetch per doctor

    // Fetch month availability for calendar color coding
    await fetchDoctorMonthAvailability(currentMonth, doctor.id);
  };

  // Clinic dropdown handlers
  const toggleClinicDropdown = () => {
    console.log(`🏥 Toggle Clinic Dropdown: availableClinics=${availableClinics.length}, appointmentType=${bookingForm.appointmentType}, showClinicDropdown=${showClinicDropdown}`);
    setShowClinicDropdown(!showClinicDropdown);
    setShowDoctorDropdown(false);
  };

  const handleClinicSelect = (clinic) => {
    setBookingForm(prev => ({
      ...prev,
      clinicLocation: clinic.clinic_name,
      clinicianId: '',  // Reset clinician when clinic changes
      clinicianName: ''
    }));
    setShowClinicDropdown(false);
    // Fetch clinicians for the selected clinic
    fetchCliniciansByClinic(clinic.clinic_name);
  };

  // Fetch clinicians when clinic location changes
  const fetchCliniciansByClinic = async (clinicName) => {
    if (!clinicName) {
      setClinicians([]);
      return;
    }

    try {
      setLoadingClinicians(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/clinicians/by-clinic-name/${encodeURIComponent(clinicName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch clinicians');
      }

      const data = await response.json();
      setClinicians(data);
      console.log(`👨‍⚕️ Admin: Fetched ${data.length} clinicians for clinic: ${clinicName}`);
      console.log('👨‍⚕️ Admin: Clinician data received:', JSON.stringify(data, null, 2));
      // Log each clinician's full_name specifically
      data.forEach((clinician, index) => {
        console.log(`Clinician ${index}: user_id=${clinician.user_id}, full_name="${clinician.full_name}", firstname="${clinician.firstname}", lastname="${clinician.lastname}"`);
      });
    } catch (error) {
      console.error('Error fetching clinicians:', error);
      toast.error('Failed to load clinicians for selected clinic');
      setClinicians([]);
    } finally {
      setLoadingClinicians(false);
    }
  };

  const handleClinicianSelect = (clinicianId) => {
    const selectedClinician = clinicians.find(c => c.id === parseInt(clinicianId));
    if (selectedClinician) {
      setBookingForm(prev => ({
        ...prev,
        clinicianId: clinicianId,
        clinicianName: selectedClinician.full_name || `${selectedClinician.firstname} ${selectedClinician.lastname}`.trim()
      }));
      console.log(`👨‍⚕️ Admin: Selected clinician: ${selectedClinician.full_name} (Clinician ID: ${clinicianId}, User ID: ${selectedClinician.user_id})`);
    }
  };

  // Filter past time slots for today's date (like patient module)
  const filterPastSlotsForToday = (times, selectedDate) => {
    const today = new Date();
    const selected = new Date(selectedDate);
    
    // Check if selected date is today (compare date only)
    const isToday = today.toDateString() === selected.toDateString();
    
    if (!isToday) {
      return times; // Return all times for non-today dates
    }
    
    // For today, filter out past time slots
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    return times.filter(time => {
      if (!time) return false;
      
      const [slotHour, slotMinute] = time.split(':').map(Number);
      const slotTimeInMinutes = slotHour * 60 + slotMinute;
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      
      // Keep slots that start after current time
      return slotTimeInMinutes > currentTimeInMinutes;
    });
  };

  // Fetch doctor's available time slots for a specific date
  const fetchDoctorSlots = async (dateObj) => {
    try {
      setAvailableTimes([]);
      setLoadingTimes(true);
      
      if (!bookingForm.doctor_id) return;
      
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const isoDate = `${year}-${month}-${day}`;
      
      const times = await adminBookingService.getDoctorDaySlots(bookingForm.doctor_id, isoDate);
      
      // Filter out past time slots for today
      const filteredTimes = filterPastSlotsForToday(times, dateObj);
      setAvailableTimes(filteredTimes);
      
      if (filteredTimes.length === 0) {
        addToast('No available slots for selected date', 'warning');
      }
    } catch (error) {
      console.error('Error fetching doctor slots:', error);
      addToast('Failed to load available times', 'error');
      setAvailableTimes([]);
    } finally {
      setLoadingTimes(false);
    }
  };

  // Fetch doctor's month availability for calendar color coding
  const fetchDoctorMonthAvailability = async (monthDate, doctorId) => {
    try {
      const actualDoctorId = doctorId || bookingForm.doctor_id;
      if (!actualDoctorId) {
        setDoctorMonthAvailability(null);
        return;
      }
      
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth() + 1;
      
      const availability = await adminBookingService.getDoctorMonthAvailability(actualDoctorId, year, month);
      setDoctorMonthAvailability(availability);
    } catch (error) {
      console.error('Failed to load doctor month availability:', error);
      setDoctorMonthAvailability(null);
    }
  };

  // Function to format display values for confirmation block
  const formatDisplayValue = (field, value) => {
    const formatMap = {
      appointmentType: {
        'general-telehealth': 'General Telehealth',
        'clinical-telehealth': 'Clinical Telehealth',
        'home-care': 'Home Care',
        'followUp': 'Follow-up'
      },
      scheduleType: {
        'in-person': 'In Person',
        'telehealth': 'Telehealth',
        'standard': 'Standard'
      },
      clinicLocation: value
    };

    return formatMap[field]?.[value] || value;
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handleMonthChange = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'next') {
        newMonth.setMonth(prev.getMonth() + 1);
      } else {
        newMonth.setMonth(prev.getMonth() - 1);
      }
      
      // Fetch availability for the new month if doctor is selected
      if (bookingForm.doctor_id) {
        fetchDoctorMonthAvailability(newMonth, bookingForm.doctor_id);
      }
      
      return newMonth;
    });
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableTimes([]);
  };

  const handleDateSelect = async (day) => {
    setSelectedDate(day);
    setSelectedTime(null);
    
    // Fetch available times for selected doctor + date (like patient module)
    if (bookingForm.doctor_id) {
      const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      await fetchDoctorSlots(dateObj);
    } else {
      setAvailableTimes([]);
    }
  };

  const isSelected = (day) => {
    return selectedDate === day;
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentMonth.getMonth() === today.getMonth() && 
           currentMonth.getFullYear() === today.getFullYear();
  };

  // Check if a day has available slots (for green background)
  const hasAvailableSlots = (day) => {
    if (!doctorMonthAvailability || !doctorMonthAvailability.days) return false;
    
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    
    // Only show availability for future dates
    if (currentDate < today) return false;
    
    const dayData = doctorMonthAvailability.days.find(d => {
      const availDate = new Date(d.avail_date);
      availDate.setHours(0, 0, 0, 0);
      return availDate.getTime() === currentDate.getTime();
    });
    
    return dayData && dayData.is_available && dayData.available_slots_count > 0;
  };

  // Check if a day is unavailable or has no slots (for red background)
  const isUnavailableDay = (day) => {
    if (!doctorMonthAvailability || !doctorMonthAvailability.days) return false;
    
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    
    // Only show unavailability for future dates
    if (currentDate < today) return false;
    
    const dayData = doctorMonthAvailability.days.find(d => {
      const availDate = new Date(d.avail_date);
      availDate.setHours(0, 0, 0, 0);
      return availDate.getTime() === currentDate.getTime();
    });
    
    return dayData && (!dayData.is_available || dayData.available_slots_count === 0);
  };

  // Format time slot for display (like patient module)
  const formatTimeSlot = (timeSlot) => {
    const [hours, minutes] = timeSlot.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Handle booking confirmation
  const handleConfirmBooking = async () => {
    if (!selectedDate || !selectedTime || !bookingForm.reason || !bookingForm.appointmentType || !selectedPatient) {
      setBookingError('Please fill in all required fields and select a patient, date, and time');
      return;
    }

    // Check clinic location requirement for clinical telehealth
    if (bookingForm.appointmentType === 'clinical-telehealth' && !bookingForm.clinicLocation) {
      setBookingError('Clinic location is required for Clinical Telehealth appointments');
      return;
    }

    try {
      setBookingLoading(true);
      setBookingError('');

      // Format date
      const year = currentMonth.getFullYear();
      const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
      const dayStr = String(selectedDate).padStart(2, '0');
      const formattedDate = `${year}-${month}-${dayStr}`;

      // Debug patient data to understand the structure
      console.log('🔍 Selected patient data:', selectedPatient);
      
      // Extract and validate patient_id
      // Use the numeric database ID for booking (selectedPatient.id)
      // patient_id field like "P000002" is for display, not for database relations
      const rawPatientId = selectedPatient.id; // Always use the numeric database ID
      const parsedPatientId = parseInt(rawPatientId);
      
      // Extract and validate user_id  
      const rawUserId = selectedPatient.user_id;
      const parsedUserId = parseInt(rawUserId);
      
      console.log('🔍 Using database ID for patient_id:', rawPatientId, 'Display patient_id:', selectedPatient.patient_id);
      console.log('🔍 Raw patient_id (DB):', rawPatientId, 'Parsed:', parsedPatientId);
      console.log('🔍 Raw user_id:', rawUserId, 'Parsed:', parsedUserId);
      
      // Validate that we have valid integers
      if (!parsedPatientId || isNaN(parsedPatientId)) {
        throw new Error(`Invalid patient database ID: ${rawPatientId}. Please search for the patient again.`);
      }
      
      if (!parsedUserId || isNaN(parsedUserId)) {
        throw new Error(`Invalid user ID: ${rawUserId}. Please search for the patient again.`);
      }
      
      const bookingData = {
        user_id: parsedUserId,
        patient_id: parsedPatientId,
        reason: bookingForm.reason || '',
        appointment_type: formatDisplayValue('appointmentType', bookingForm.appointmentType) || '',
        schedule_type: formatDisplayValue('scheduleType', bookingForm.scheduleType) || '',
        specialty: bookingForm.specialty || '',
        doctor: bookingForm.doctor || '',
        doctor_id: bookingForm.doctor_id ? (Number.isInteger(parseInt(bookingForm.doctor_id)) && parseInt(bookingForm.doctor_id) > 0 ? parseInt(bookingForm.doctor_id) : null) : null,
        clinic_location: formatDisplayValue('clinicLocation', bookingForm.clinicLocation) || '',
        clinician_id: bookingForm.clinicianId ? parseInt(bookingForm.clinicianId) : null,
        clinician_name: bookingForm.clinicianName || null,
        appointment_date: formattedDate,
        appointment_time: selectedTime,
        status: 'Scheduled'
      };

      console.log('Creating admin booking with data:', bookingData);
      
      // Create booking via API
      const newBooking = await adminBookingService.createBookingForPatient(bookingData);
      console.log('Booking created successfully:', newBooking);
      
      // Show success toast
      addToast(`Booking created successfully for ${selectedPatient.name}`, 'success');
      
      // Reset form
      setSelectedDate(null);
      setSelectedTime(null);
      setAvailableTimes([]);
      setBookingForm({
        patientId: '',
        patientName: '',
        reason: '',
        appointmentType: '',
        scheduleType: '',
        specialty: '',
        doctor: '',
        doctor_id: '',
        clinicLocation: ''
      });
      setSelectedPatient(null);
      setPatientId('');
      setPatientName('');
      setPatientDOB('');
      setDoctors([]);
      setAvailableClinics([]);
      setSpecialties([]);
      
      // Re-fetch specialties for next booking
      setTimeout(async () => {
        try {
          const specialtiesData = await adminBookingService.getSpecialties();
          setSpecialties(specialtiesData);
        } catch (error) {
          console.error('Error re-fetching specialties:', error);
        }
      }, 100);

    } catch (error) {
      console.error('❌ Error creating booking:', error);
      console.error('❌ Error stack:', error.stack);
      console.error('❌ Error type:', typeof error);
      
      let errorMessage = 'Failed to create booking. Please try again.';
      if (error.message && error.message !== '[object Object]') {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      console.error('❌ Final error message to display:', errorMessage);
      setBookingError(`Booking Failed - ${errorMessage}`);
      addToast(`Booking failed: ${errorMessage}`, 'error');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Blue header */}
      <div className="bg-blue-600 text-white py-1 px-2">
        <h1 className="text-xl font-bold text-center">Make a Booking</h1>
      </div>

      <div className="flex">
        {/* Left Column - Patient Search (25%) */}
        <div className="w-1/4 bg-gray-50 min-h-screen p-4">
          <div className="bg-white rounded-lg border border-blue-200 p-3 mt-3">
            <h2 className="text-lg font-semibold text-blue-600 mb-3">Patient</h2>
            
            <div className="space-y-3">
              {/* Patient ID */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Patient ID
                </label>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={patientId}
                    onChange={(e) => setPatientId(e.target.value)}
                    placeholder="Patient ID"
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleNewPatient}
                    className="px-2 py-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded transition-all duration-200 text-xs flex items-center space-x-1"
                  >
                    <RotateCcw className="h-3 w-3" />
                    <span>Reset Fields</span>
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-2"></div>

              {/* Patient Name and DOB in one line */}
              <div>
                <div className="flex gap-1 mb-1">
                  <label className="block text-xs font-medium text-gray-700 w-[60%]">
                    Patient Name
                  </label>
                  <label className="block text-xs font-medium text-gray-700 w-[40%]">
                    Patient DOB
                  </label>
                </div>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    placeholder="Patient Name"
                    className="w-[60%] px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <input
                    type="date"
                    value={patientDOB}
                    onChange={(e) => setPatientDOB(e.target.value)}
                    placeholder="DOB"
                    className="w-[40%] px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="mt-2">
                  <button
                    onClick={handlePatientSearch}
                    disabled={patientSearchLoading}
                    className="w-full px-2 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs flex items-center justify-center gap-1 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Search className="h-3 w-3" />
                    {patientSearchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>

                {/* Patient Search Error */}
                {patientSearchError && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-xs">{patientSearchError}</p>
                  </div>
                )}
              </div>

              {/* Selected Patient Display */}
              {selectedPatient && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <h3 className="font-medium text-green-800 mb-2">Selected Patient</h3>
                  <div className="space-y-1">
                    <p className="text-sm text-green-600">
                      <span className="font-medium">ID:</span> {selectedPatient.patient_id || selectedPatient.id}
                    </p>
                    <p className="text-sm text-green-600">
                      <span className="font-medium">Name:</span> {selectedPatient.name}
                    </p>
                    <p className="text-sm text-green-600">
                      <span className="font-medium">DOB:</span> {selectedPatient.dob ? new Date(selectedPatient.dob).toLocaleDateString() : 'N/A'}
                    </p>
                    {selectedPatient.user_email && (
                      <p className="text-sm text-green-600">
                        <span className="font-medium">Email:</span> {selectedPatient.user_email}
                      </p>
                    )}
                    {selectedPatient.phone && (
                      <p className="text-sm text-green-600">
                        <span className="font-medium">Phone:</span> {selectedPatient.phone}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Booking Form (75%) */}
        <div className="w-3/4 bg-gray-50 p-6">
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-6">
              {/* Success and Error Messages */}
              {bookingSuccess && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">Booking created successfully!</span>
                  </div>
                </div>
              )}
              
              {bookingError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">{bookingError}</span>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Standard Appointment Section - Only for Standard patients or no patient selected */}
                {(!selectedPatient || selectedPatient.patient_type === 'Standard') && (
                  <div className="bg-blue-50 p-4 rounded-md">
                    <h4 className="font-medium text-blue-600 mb-4">Appointment</h4>
                  
                  {/* Patient ID and Patient Name row */}
                  <div className="flex gap-3 mb-3">
                    <div className="w-1/2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Patient ID
                      </label>
                      <input
                        type="text"
                        value={bookingForm.patientId}
                        onChange={(e) => handleBookingFormChange('patientId', e.target.value)}
                        placeholder="Patient ID"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        readOnly
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Patient Name
                      </label>
                      <input
                        type="text"
                        value={bookingForm.patientName}
                        onChange={(e) => handleBookingFormChange('patientName', e.target.value)}
                        placeholder="Patient Name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        readOnly
                      />
                    </div>
                  </div>

                  {/* Reason and Appointment Type row */}
                  <div className="flex gap-3 mb-3">
                    <div className="w-[75%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason For Appointment
                      </label>
                      <input
                        type="text"
                        value={bookingForm.reason}
                        onChange={(e) => handleBookingFormChange('reason', e.target.value)}
                        placeholder="Please describe reason for appointment e.g. Flu symptoms."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="w-[25%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Appointment Type
                      </label>
                      <select
                        value={bookingForm.appointmentType}
                        onChange={(e) => handleBookingFormChange('appointmentType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Please Select</option>
                        <option value="General Telehealth">General Telehealth</option>
                        <option value="Clinical Telehealth">Clinical Telehealth</option>
                        <option value="Home Care" disabled={selectedPatient?.patient_type === 'Standard'}>
                          Home Care {selectedPatient?.patient_type === 'Standard' && '(Aged Care Only)'}
                        </option>
                      </select>
                    </div>
                  </div>

                  {/* Schedule Type, Specialty, Doctor, Clinic Location row */}
                  <div className="flex gap-3">
                    <div className="w-[20%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Schedule Type
                      </label>
                      <select
                        value={bookingForm.scheduleType}
                        onChange={(e) => handleBookingFormChange('scheduleType', e.target.value)}
                        disabled={bookingForm.appointmentType !== ''}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          bookingForm.appointmentType !== '' ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      >
                        <option value="">Please Select</option>
                        <option value="in-person">In Person</option>
                        <option value="telehealth">Telehealth</option>
                      </select>
                    </div>
                    <div className="w-[20%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Specialty
                      </label>
                      <select
                        value={bookingForm.specialty}
                        onChange={(e) => handleSpecialtyChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Please Select</option>
                        {loadingSpecialties ? (
                          <option value="">Loading specialties...</option>
                        ) : (
                          specialties.map((specialty) => (
                            <option key={specialty} value={specialty}>
                              {specialty}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <div className="w-[30%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Doctor
                      </label>
                      <div className="relative" ref={doctorDropdownRef}>
                        <input
                          type="text"
                          value={bookingForm.doctor}
                          readOnly
                          placeholder={loadingDoctors ? "Loading doctors..." : "Select a specialty first"}
                          className="w-full px-3 py-1.5 pr-16 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button 
                          onClick={toggleDoctorDropdown}
                          disabled={doctors.length === 0}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          Change
                        </button>
                        
                        {/* Doctor Dropdown */}
                        {showDoctorDropdown && doctors.length > 0 && (
                          <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                            {doctors.map((doctor) => (
                              <div
                                key={doctor.id}
                                className="px-3 py-2 cursor-pointer hover:bg-blue-100 border-b border-gray-100 last:border-b-0"
                                onClick={() => handleDoctorSelect(doctor)}
                              >
                                <div className="font-medium">{doctor.firstname} {doctor.lastname}</div>
                                <div className="text-xs text-gray-600">{doctor.specialties}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-[30%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Clinic Location
                      </label>
                      <div className="relative" ref={clinicDropdownRef}>
                        <input
                          type="text"
                          value={bookingForm.clinicLocation}
                          readOnly={bookingForm.appointmentType === 'General Telehealth'}
                          onClick={bookingForm.appointmentType === 'Clinical Telehealth' ? toggleClinicDropdown : undefined}
                          placeholder={
                            bookingForm.appointmentType === 'General Telehealth' 
                              ? "Not required for General Telehealth" 
                              : loadingClinics 
                                ? "Loading clinics..." 
                                : "Select clinic"
                          }
                          className={`w-full px-3 py-1.5 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            bookingForm.appointmentType === 'General Telehealth' 
                              ? 'bg-gray-100 cursor-not-allowed' 
                              : 'bg-gray-50 cursor-pointer'
                          }`}
                        />
                        <button 
                          onClick={toggleClinicDropdown}
                          disabled={availableClinics.length === 0 || bookingForm.appointmentType === 'General Telehealth'}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                          title={`Debug: availableClinics=${availableClinics.length}, appointmentType=${bookingForm.appointmentType}`}
                        >
                          Change
                        </button>
                        
                        {/* Clinic Dropdown */}
                        {showClinicDropdown && availableClinics.length > 0 && (
                          <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                            {availableClinics.map((clinic) => (
                              <div
                                key={clinic.clinic_id}
                                className="px-3 py-2 cursor-pointer hover:bg-blue-100 border-b border-gray-100 last:border-b-0"
                                onClick={() => handleClinicSelect(clinic)}
                              >
                                <div className="font-medium">{clinic.clinic_name}</div>
                                <div className="text-xs text-gray-600">{clinic.clinic_address}</div>
                                {clinic.is_primary_clinic && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                    Primary
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Clinician Select Field - Only show for Clinical Telehealth and when clinic is selected */}
                  {bookingForm.clinicLocation && bookingForm.appointmentType === 'Clinical Telehealth' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Clinician <span className="text-gray-500">(Optional)</span>
                      </label>
                      <select
                        value={bookingForm.clinicianId}
                        onChange={(e) => handleClinicianSelect(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={loadingClinicians || clinicians.length === 0}
                      >
                        <option value="">No Specific Clinician (Any Available)</option>
                        {loadingClinicians ? (
                          <option disabled>Loading clinicians...</option>
                        ) : clinicians.length === 0 ? (
                          <option disabled>No clinicians available at this clinic</option>
                        ) : (
                          clinicians.map((clinician) => (
                            <option key={clinician.id} value={clinician.id}>
                              {clinician.full_name || `${clinician.firstname || ''} ${clinician.lastname || ''}`.trim() || `Clinician ${clinician.id}`} {clinician.is_primary_clinic && '★ Primary'}
                            </option>
                          ))
                        )}
                      </select>
                      {clinicians.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {clinicians.length} clinician{clinicians.length !== 1 ? 's' : ''} available at {bookingForm.clinicLocation}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Month, Time and Confirmation Section - Only for Standard patients */}
                {(!selectedPatient || selectedPatient.patient_type === 'Standard') && (
                  <div className="bg-blue-50 p-4 rounded-md">
                    <div className="flex gap-4">
                      {/* Month/Calendar Section - 35% */}
                      <div className="w-[35%]">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium text-blue-600">
                          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h4>
                        <div className="flex space-x-2">
                          <button 
                            className="text-gray-600 hover:text-gray-800 p-1 rounded hover:bg-gray-100"
                            onClick={() => handleMonthChange('prev')}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          <button 
                            className="text-gray-600 hover:text-gray-800 p-1 rounded hover:bg-gray-100"
                            onClick={() => handleMonthChange('next')}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-7 gap-1 text-xs mb-2">
                        <div className="text-center font-medium text-gray-600">Sun</div>
                        <div className="text-center font-medium text-gray-600">Mon</div>
                        <div className="text-center font-medium text-gray-600">Tue</div>
                        <div className="text-center font-medium text-gray-600">Wed</div>
                        <div className="text-center font-medium text-gray-600">Thu</div>
                        <div className="text-center font-medium text-gray-600">Fri</div>
                        <div className="text-center font-medium text-gray-600">Sat</div>
                      </div>
                      
                      <div className="grid grid-cols-7 gap-1">
                        {/* Empty cells for days before the first day of the month */}
                        {Array.from({ length: getFirstDayOfMonth(currentMonth) }, (_, i) => (
                          <div key={`empty-${i}`} className="h-8"></div>
                        ))}
                        
                        {/* Days of the month */}
                        {Array.from({ length: getDaysInMonth(currentMonth) }, (_, i) => {
                          const day = i + 1;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                          currentDate.setHours(0, 0, 0, 0);
                          const isPastDate = currentDate < today;
                          
                          // Determine background color based on doctor availability (like patient module)
                          const hasSlots = hasAvailableSlots(day);
                          const isUnavailable = isUnavailableDay(day);
                          const showColorCoding = bookingForm.doctor_id && !isPastDate;
                          
                          return (
                            <div
                              key={day}
                              className={`h-8 border rounded flex items-center justify-center text-xs cursor-pointer transition-all duration-200 hover:scale-105 ${
                                isPastDate
                                  ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
                                  : isSelected(day)
                                    ? 'bg-blue-500 text-white border-blue-500 shadow-lg transform scale-105'
                                    : isToday(day) && showColorCoding
                                      ? hasSlots
                                        ? 'bg-green-400 text-gray-800 border-green-400 shadow-md hover:bg-green-500'
                                        : isUnavailable
                                          ? 'bg-red-400 text-gray-800 border-red-300 shadow-md hover:bg-red-500'
                                          : 'bg-green-400 text-gray-800 border-green-300 hover:bg-green-500'
                                      : showColorCoding
                                        ? hasSlots
                                          ? 'bg-green-400 text-gray-800 border-green-300 hover:bg-green-500 shadow-sm'
                                          : isUnavailable
                                            ? 'bg-red-400 text-gray-800 border-red-300 hover:bg-red-500 shadow-sm'
                                            : 'bg-white text-gray-700 border-blue-200 hover:bg-blue-100'
                                        : 'bg-white text-gray-700 border-blue-200 hover:bg-blue-100'
                              }`}
                              onClick={() => !isPastDate && handleDateSelect(day)}
                            >
                              {day}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Calendar Legend (only show when doctor is selected) */}
                      {bookingForm.doctor_id && (
                        <div className="mt-3 px-1">
                          <div className="flex justify-center space-x-3 text-xs">
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 bg-green-400 rounded"></div>
                              <span className="text-gray-600">Available</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 bg-red-400 rounded"></div>
                              <span className="text-gray-600">Unavailable</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 bg-blue-500 rounded"></div>
                              <span className="text-gray-600">Selected</span>
                            </div>
                          </div>
                        </div>
                      )}
                      </div>

                      {/* Time Section - 25% */}
                      <div className="w-[25%]">
                      <h4 className="font-medium text-blue-600 mb-3">Time</h4>
                      <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-2">
                        {!selectedDate ? (
                          <div className="col-span-2 flex items-center justify-center p-4 text-gray-500 text-sm">
                            Please select a date first
                          </div>
                        ) : !bookingForm.doctor_id ? (
                          <div className="col-span-2 flex items-center justify-center p-4 text-gray-500 text-sm">
                            Please select a doctor first
                          </div>
                        ) : loadingTimes ? (
                          <div className="col-span-2 flex items-center justify-center p-4 text-blue-500 text-sm">
                            Loading available times...
                          </div>
                        ) : availableTimes.length === 0 ? (
                          <div className="col-span-2 flex items-center justify-center p-4 text-center">
                            <div className="text-red-500 text-sm">
                              <div className="mb-1">No available slots</div>
                              <div className="text-xs text-gray-500">
                                Please select a different date
                              </div>
                            </div>
                          </div>
                        ) : (
                          availableTimes.map((time) => (
                            <div
                              key={time}
                              className={`h-8 border border-blue-200 rounded flex items-center justify-center text-xs cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md ${
                                selectedTime === time 
                                  ? 'bg-blue-500 text-white shadow-lg transform scale-105' 
                                  : 'bg-white text-gray-700 hover:bg-blue-100'
                              }`}
                              onClick={() => setSelectedTime(time)}
                            >
                              {formatTimeSlot(time)}
                            </div>
                          ))
                        )}
                      </div>
                      </div>

                      {/* Confirmation Section - 40% */}
                      <div className="w-[40%]">
                      <h4 className="font-medium text-blue-600 mb-3">Confirmation</h4>
                      <div className="bg-white p-3 rounded border space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">Patient:</span>
                          <span>{selectedPatient ? selectedPatient.name : 'Not selected'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Reason:</span>
                          <span className="text-right">{bookingForm.reason || 'Not specified'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Type:</span>
                          <span>{bookingForm.appointmentType || 'Not selected'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Doctor:</span>
                          <span className="text-right">{bookingForm.doctor || 'Not selected'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Date:</span>
                          <span>
                            {selectedDate 
                              ? `${selectedDate}/${currentMonth.getMonth() + 1}/${currentMonth.getFullYear()}`
                              : 'Not selected'
                            }
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Time:</span>
                          <span>{selectedTime || 'Not selected'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Clinic:</span>
                          <span className="text-right">{bookingForm.clinicLocation || 'Not selected'}</span>
                        </div>
                        
                        <div className="pt-3 border-t">
                          <button
                            onClick={handleConfirmBooking}
                            disabled={bookingLoading || !selectedDate || !selectedTime || !bookingForm.reason || !selectedPatient}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                          >
                            {bookingLoading ? 'Creating...' : 'Confirm Booking'}
                          </button>
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Home Care Booking Section - Only for Aged Care patients */}
                {selectedPatient?.patient_type === 'Aged Care' && (
                  <HomeCareBookingSection
                    patientData={selectedPatient}
                    onBookingSuccess={(response) => {
                      console.log('✅ Home Care bookings created:', response);
                      toast.success(response.message);
                      
                      // Reset patient selection
                      setSelectedPatient(null);
                      setPatientId('');
                      setPatientName('');
                      setPatientDOB('');
                      setBookingForm({
                        patientId: '',
                        patientName: '',
                        reason: '',
                        appointmentType: '',
                        scheduleType: '',
                        specialty: '',
                        doctor: '',
                        doctor_id: '',
                        clinicLocation: ''
                      });
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`max-w-md p-4 rounded-lg shadow-lg flex items-center space-x-3 transform transition-all duration-300 ${
              toast.type === 'success'
                ? 'bg-green-500 text-white'
                : toast.type === 'error'
                ? 'bg-red-500 text-white'
                : toast.type === 'warning'
                ? 'bg-yellow-500 text-white'
                : 'bg-blue-500 text-white'
            }`}
          >
            <div className="flex-shrink-0">
              {toast.type === 'success' && <CheckCircle className="h-5 w-5" />}
              {toast.type === 'error' && <AlertCircle className="h-5 w-5" />}
              {toast.type === 'warning' && <AlertCircle className="h-5 w-5" />}
              {toast.type === 'info' && <AlertCircle className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium break-words">{toast.message}</p>
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="flex-shrink-0 hover:bg-black hover:bg-opacity-10 rounded p-1 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminBookingPage;
